import { useState } from 'react';
import { ChevronRight, ChevronDown, Plus, Pencil, Trash2 } from 'lucide-react';
import { useCategories } from '../hooks/useCategories.js';
import { useMappingRules, useDeleteMappingRule } from '../hooks/useMappingRules.js';
import { buildCategoryTree } from '../lib/buildCategoryTree.js';
import CategoryCustomModal from '../components/CategoryCustomModal.jsx';
import CategoryEditModal from '../components/CategoryEditModal.jsx';
import RuleEditor from '../components/RuleEditor.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import Button from '../components/Button.jsx';

// ─── Categories tree ──────────────────────────────────────────────────────────

function SourceBadge({ source }) {
  if (source === 'custom') {
    return (
      <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700">
        Custom
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
      Qonto
    </span>
  );
}

function TxCount({ count }) {
  if (!count) return null;
  return <span className="text-xs text-slate-400 tabular-nums">{count} tx</span>;
}

function CategoryTree({ onSelectCategory, selectedCategoryId }) {
  const { data, isLoading, error } = useCategories();
  const [expanded, setExpanded] = useState(new Set());
  const [createModal, setCreateModal] = useState({ open: false, parent: null, root: null });
  const [editModal, setEditModal] = useState({ open: false, category: null });

  const categories = Array.isArray(data) ? data : data?.categories ?? data?.data ?? [];
  const catById = Object.fromEntries(categories.map((c) => [c.id, c]));
  const tree = buildCategoryTree(categories).sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (isLoading) return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  if (error) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
        Erreur : {error.message}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
        {tree.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-10">Aucune catégorie</p>
        ) : (
          tree.map((root) => {
            const isOpen = expanded.has(root.id);
            return (
              <div key={root.id}>
                <div className="flex items-center gap-2 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggle(root.id)}
                    className="shrink-0 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
                    aria-label={isOpen ? 'Replier' : 'Déplier'}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <span className="flex-1 text-sm font-semibold text-slate-800 min-w-0 truncate">
                    {root.name}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <TxCount count={root.aggregated.transactions_count_30d} />
                    <SourceBadge source={root.source ?? 'qonto'} />
                  </div>
                </div>

                {isOpen && root.children.length > 0 && (
                  <div className="bg-slate-50/60 border-t border-slate-100 divide-y divide-slate-100">
                    {root.children.map((sub) => (
                      <div key={sub.id}>
                        <div className="flex items-center gap-2 pl-10 pr-4 py-2.5">
                          <button
                            type="button"
                            onClick={() => onSelectCategory(sub.id === selectedCategoryId ? null : sub.id)}
                            className={`flex-1 text-sm text-left min-w-0 truncate transition-colors ${
                              sub.id === selectedCategoryId
                                ? 'text-slate-900 font-semibold'
                                : 'text-slate-700 hover:text-slate-900'
                            }`}
                          >
                            {sub.name}
                          </button>
                          <div className="flex items-center gap-2 shrink-0">
                            <TxCount count={sub.transactions_count_30d} />
                            <SourceBadge source={sub.source ?? 'qonto'} />
                            {(sub.source === 'qonto' || !sub.source) && (
                              <button
                                type="button"
                                onClick={() => setCreateModal({ open: true, parent: sub, root })}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 transition-colors"
                                aria-label={`Ajouter une sous-catégorie sous ${sub.name}`}
                              >
                                <Plus size={12} aria-hidden />
                                Ajouter
                              </button>
                            )}
                          </div>
                        </div>

                        {sub.children.length > 0 && (
                          <div className="divide-y divide-slate-100">
                            {sub.children.map((custom) => (
                              <div
                                key={custom.id}
                                className={`flex items-center gap-2 pl-16 pr-4 py-2 cursor-pointer transition-colors ${
                                  custom.id === selectedCategoryId
                                    ? 'bg-sky-50'
                                    : 'bg-white hover:bg-slate-50'
                                }`}
                                onClick={() => onSelectCategory(custom.id === selectedCategoryId ? null : custom.id)}
                              >
                                <span className="flex-1 text-sm text-slate-600 min-w-0 truncate">
                                  {custom.name}
                                </span>
                                <div className="flex items-center gap-2 shrink-0">
                                  <TxCount count={custom.transactions_count_30d} />
                                  <SourceBadge source="custom" />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditModal({
                                        open: true,
                                        category: { ...custom, parent_name: sub.name },
                                      });
                                    }}
                                    className="rounded p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 transition-colors"
                                    aria-label={`Modifier ${custom.name}`}
                                  >
                                    <Pencil size={14} aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditModal({
                                        open: true,
                                        category: {
                                          ...custom,
                                          parent_name: catById[custom.parent_id]?.name ?? sub.name,
                                        },
                                      });
                                    }}
                                    className="rounded p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 transition-colors"
                                    aria-label={`Supprimer ${custom.name}`}
                                  >
                                    <Trash2 size={14} aria-hidden />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <CategoryCustomModal
        open={createModal.open}
        parentCategory={createModal.parent}
        rootCategory={createModal.root}
        onClose={() => setCreateModal({ open: false, parent: null, root: null })}
      />
      <CategoryEditModal
        open={editModal.open}
        category={editModal.category}
        onClose={() => setEditModal({ open: false, category: null })}
      />
    </>
  );
}

// ─── Rules panel ─────────────────────────────────────────────────────────────

function RulesPanel({ selectedCategoryId }) {
  const { data, isLoading, error } = useMappingRules();
  const { data: catData } = useCategories();
  const remove = useDeleteMappingRule();

  const [editorRule, setEditorRule] = useState(undefined); // undefined = closed, null = create, object = edit
  const isEditorOpen = editorRule !== undefined;

  const rules = Array.isArray(data) ? data : data?.rules ?? data?.data ?? [];
  const categories = Array.isArray(catData) ? catData : catData?.categories ?? catData?.data ?? [];

  const filtered = selectedCategoryId
    ? rules.filter((r) => r.category_id === selectedCategoryId)
    : rules;

  function getCatName(id) {
    return categories.find((c) => c.id === id)?.name ?? '—';
  }

  if (isLoading) return <div className="flex justify-center py-8"><LoadingSpinner /></div>;

  return (
    <div className="space-y-3">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error.message}
        </div>
      )}

      {selectedCategoryId && (
        <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
          Filtré sur : <strong>{getCatName(selectedCategoryId)}</strong>
        </p>
      )}

      {!isEditorOpen && (
        <Button onClick={() => setEditorRule(null)} className="w-full sm:w-auto">
          <Plus size={14} className="mr-1" aria-hidden /> Nouvelle règle
        </Button>
      )}

      {isEditorOpen && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">
            {editorRule ? 'Modifier la règle' : 'Nouvelle règle'}
          </h3>
          <RuleEditor
            rule={editorRule ?? undefined}
            onSave={() => setEditorRule(undefined)}
            onCancel={() => setEditorRule(undefined)}
            onDelete={() => setEditorRule(undefined)}
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm text-center py-12 text-sm text-slate-500">
          {selectedCategoryId
            ? 'Aucune règle pour cette catégorie.'
            : 'Aucune règle de catégorisation.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
          {filtered.map((rule) => (
            <div key={rule.id} className="px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 mb-1">
                  → <span className="font-medium text-slate-700">{getCatName(rule.category_id)}</span>
                  {rule.priority !== undefined && (
                    <span className="ml-2 text-slate-400">priorité {rule.priority}</span>
                  )}
                  {rule.operator && (
                    <span className="ml-2 font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-xs">
                      {rule.operator}
                    </span>
                  )}
                </p>
                {(rule.conditions ?? []).map((cond, i) => (
                  <p key={i} className="text-sm text-slate-700">
                    <span className="font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-xs mr-1.5">
                      {cond.match_type}
                    </span>
                    {cond.match_value}
                  </p>
                ))}
                {/* Legacy single-condition display */}
                {!rule.conditions?.length && rule.pattern && (
                  <p className="text-sm text-slate-700">
                    <span className="font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-xs mr-1.5">
                      {rule.match_type}
                    </span>
                    {rule.pattern}
                  </p>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setEditorRule(rule)}
                  className="rounded p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 transition-colors"
                  aria-label="Modifier la règle"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => remove.mutate(rule.id)}
                  className="rounded p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 transition-colors"
                  aria-label="Supprimer la règle"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CategoriesRulesTabPage() {
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  // Mobile: 'categories' | 'rules'
  const [mobileTab, setMobileTab] = useState('categories');

  return (
    <div>
      {/* Mobile tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-200 lg:hidden">
        {['categories', 'rules'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setMobileTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              mobileTab === tab
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {tab === 'categories' ? 'Catégories' : 'Règles'}
          </button>
        ))}
      </div>

      {/* Desktop: 2 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonne gauche : catégories */}
        <div className={mobileTab === 'categories' ? 'block' : 'hidden lg:block'}>
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Gérer les catégories</h2>
          <p className="text-xs text-slate-500 mb-3">
            Cliquer sur une catégorie custom (niveau 3) filtre les règles associées à droite.
          </p>
          <CategoryTree
            onSelectCategory={setSelectedCategoryId}
            selectedCategoryId={selectedCategoryId}
          />
        </div>

        {/* Colonne droite : règles */}
        <div className={mobileTab === 'rules' ? 'block' : 'hidden lg:block'}>
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Règles de catégorisation</h2>
          <RulesPanel selectedCategoryId={selectedCategoryId} />
        </div>
      </div>
    </div>
  );
}
